import React, { useState } from 'react';
import { Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Message sent successfully.');
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: '', email: '', subject: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO 
        title="Contact - Get in Touch"
        description="Contact CreativeDirectorKhan | KHAN for film production, digital marketing, branding, web development, and consulting services. Serving clients worldwide."
        keywords="contact creative director, hire cinematographer, film production, digital marketing services, branding consultation, worldwide"
        canonical="https://creativedirectorkhan.com/contact"
      />
      {/* Hero Header */}
      <section className="pt-32 pb-20 px-8 md:px-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-white/[0.02] to-transparent" />
        <div className="max-w-7xl mx-auto relative">
          <div className="max-w-3xl">
            <p className="text-white/40 text-xs tracking-[0.4em] uppercase mb-6">Contact</p>
            <h1 className="font-serif text-6xl md:text-8xl lg:text-[120px] font-light text-white leading-[0.85] mb-8">
              Get in
              <span className="block text-white/40 italic">Touch</span>
            </h1>
            <p className="text-white/50 text-xl max-w-lg leading-relaxed">
              Have a project in mind? Let&apos;s discuss how we can bring your vision to life.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-8 md:px-12">
          <div className="grid lg:grid-cols-5 gap-16 lg:gap-24">
            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-12">
              {/* Location */}
              <div>
                <p className="text-white/30 text-xs tracking-[0.3em] uppercase mb-4">Availability</p>
                <p className="text-white/70 text-lg">Serving Clients Worldwide</p>
                <p className="text-white/40 text-sm mt-1">Available for remote & on-site projects</p>
              </div>

              {/* Decorative */}
              <div className="hidden lg:block pt-8">
                <div className="w-24 h-24 border border-white/10" />
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-32 text-center border border-white/5 bg-white/[0.01]">
                  <CheckCircle className="w-16 h-16 text-white/40 mb-8" />
                  <h3 className="font-serif text-3xl text-white mb-3">Thank You</h3>
                  <p className="text-white/40 max-w-sm">Your message has been sent successfully. I&apos;ll be in touch soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-10">
                  <div className="grid md:grid-cols-2 gap-10">
                    <div className="group">
                      <label className="text-white/50 text-xs tracking-[0.2em] uppercase mb-4 block">
                        Name
                      </label>
                      <input
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-0 border-b border-white/20 py-4 text-white text-lg placeholder:text-white/40 focus:border-white/50 focus:outline-none transition-colors duration-500"
                        placeholder="Your name"
                      />
                    </div>
                    <div className="group">
                      <label className="text-white/50 text-xs tracking-[0.2em] uppercase mb-4 block">
                        Email
                      </label>
                      <input
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-transparent border-0 border-b border-white/20 py-4 text-white text-lg placeholder:text-white/40 focus:border-white/50 focus:outline-none transition-colors duration-500"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="group">
                    <label className="text-white/50 text-xs tracking-[0.2em] uppercase mb-4 block">
                      Subject
                    </label>
                    <input
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full bg-transparent border-0 border-b border-white/20 py-4 text-white text-lg placeholder:text-white/40 focus:border-white/50 focus:outline-none transition-colors duration-500"
                      placeholder="Project inquiry"
                    />
                  </div>

                  <div className="group">
                    <label className="text-white/50 text-xs tracking-[0.2em] uppercase mb-4 block">
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full bg-transparent border-0 border-b border-white/20 py-4 text-white text-lg placeholder:text-white/40 focus:border-white/50 focus:outline-none transition-colors duration-500 resize-none"
                      placeholder="Tell me about your project..."
                    />
                  </div>

                  <div className="pt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex items-center gap-4 px-12 py-5 border border-white/20 text-white/90 text-sm tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border border-current border-t-transparent rounded-full animate-spin" />
                          <span>Sending...</span>
                        </>
                      ) : (
                        <>
                          <span>Send Message</span>
                          <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-500" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
